import type { CodingStatus } from './coding-events';

/**
 * FRONT-2721: past this much silence a build reads as possibly stuck. A single
 * shell step may legitimately run quiet for its 5-minute cap
 * (`EXEC_TIMEOUT_CAP_MS`, src/coding/sandbox-tools.ts), so this sits well past it.
 * A user's own MCP tool has no cap here; raising any tool limit means revisiting this.
 */
export const STUCK_AFTER_MS = 10 * 60_000;

export const STUCK_LABEL = 'May be stuck';
export const STUCK_HINT = 'Stop it, then send your message again.';
const KEEPS_GOING = 'You can close this window, the build keeps going.';
/** FRONT-3084: the line while the background check runs. */
export const CHECKING_TEXT =
  'Your link already works. EDDY is checking the rest of the app in the background. Send a message any time; the check stops for it.';

export type BuildLiveness =
  | { kind: 'working'; text: string }
  | { kind: 'stuck'; text: string; hint: string };

export interface BuildLivenessInput {
  status: CodingStatus | null;
  /** ISO time of the newest step on the tape; absent until the session GET carries it. */
  lastActivityAt: string | null | undefined;
  now: number;
}

/**
 * The working line and the lamp's stuck state as ONE decision: a build is shown
 * as stuck iff this returns `stuck`, and that variant always carries the way out.
 */
export function buildLiveness({ status, lastActivityAt, now }: BuildLivenessInput): BuildLiveness | null {
  if (status !== 'working' && status !== 'checking') return null;
  const at = lastActivityAt ? Date.parse(lastActivityAt) : NaN;
  const ageMs = Number.isFinite(at) ? Math.max(0, now - at) : NaN;
  // FRONT-3084: a silent background check gets the same way out as a build.
  if (ageMs >= STUCK_AFTER_MS) {
    return {
      kind: 'stuck',
      text: `No activity for ${Math.floor(ageMs / 60_000)} min — this build may be stuck.`,
      hint: STUCK_HINT,
    };
  }
  if (status === 'checking') return { kind: 'working', text: CHECKING_TEXT };
  if (!Number.isFinite(ageMs)) return { kind: 'working', text: `EDDY is working — ${lowerFirst(KEEPS_GOING)}` };
  return { kind: 'working', text: `EDDY is working · last activity ${formatActivityAge(ageMs)}. ${KEEPS_GOING}` };
}

/**
 * Restates the server's last-activity time on the browser's clock, so a skewed
 * laptop clock can neither fake a stuck build nor hide one.
 */
export function toLocalActivityAt(
  lastActivityAt: string | null | undefined,
  serverNow: string | null | undefined,
  clientNow: number,
): string | null | undefined {
  const at = lastActivityAt ? Date.parse(lastActivityAt) : NaN;
  const server = serverNow ? Date.parse(serverNow) : NaN;
  if (!Number.isFinite(at) || !Number.isFinite(server)) return lastActivityAt;
  return new Date(clientNow - (server - at)).toISOString();
}

export function formatActivityAge(ageMs: number): string {
  if (ageMs < 10_000) return 'just now';
  if (ageMs < 60_000) return `${Math.floor(ageMs / 1000)} s ago`;
  return `${Math.floor(ageMs / 60_000)} min ago`;
}

function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}
